import { useQueryClient } from "react-query";

import formatPropertyName from "utils/column/formatPropertyName";
import { parseShapefileProperty } from "utils/column/parseShapefileProperty";

import { getUserSetting, updateUserSetting } from "api/userSettings";

import { EntityKind } from "models/entityKind";

import { useUserContext } from "components/user/context";
import { updateSettingFocus } from "components/user/context/reducer";
import {
  updateSettingFacilityFocus,
  updateSettingFacilityMapTooltipFields,
  updateSettingMapTooltipFields
} from "components/user/context/reducer/UserReducer";
import { useUserSettingsQueryKeys } from "components/user/queries";

import {
  DEFAULT_FACILITY_FOCUS_TILES_SETTING,
  DEFAULT_FACILITY_MAP_TOOLTIP_FIELDS_SETTING,
  DEFAULT_FOCUS_TILES_SETTING,
  DEFAULT_MAP_TOOLTIP_FIELDS_SETTING
} from "../constants/userSetting.constants";
import { DefaultFocusTiles, DefaultMapTooltipFields } from "../models";
import {
  DEFAULTS_PURGE_FAILURE_MESSAGES,
  reportDefaultsPurgeFailure
} from "./reportDefaultsPurgeFailure";

// Serializes purge operations per setting key (module-level, not per hook
// instance - concurrent deletes can originate from separate rows, each with
// their own hook instance via UserFieldAction). Without this, two fields
// deleted in quick succession can both read the same pre-deletion list,
// then whichever PUT lands last silently resurrects the other's removal.
const purgeQueues = new Map<string, Promise<void>>();

function runSerialized(key: string, task: () => Promise<void>): Promise<void> {
  const previous = purgeQueues.get(key) ?? Promise.resolve();
  const next = previous.then(async () => {
    try {
      await task();
    } catch (error) {
      // Every async purge failure funnels through here.
      reportDefaultsPurgeFailure(DEFAULTS_PURGE_FAILURE_MESSAGES.fieldPurgeFailed, error);
    }
  });
  purgeQueues.set(key, next);
  return next;
}

/**
 * Predicate over a persisted entry, deciding whether it belongs to something
 * that was just deleted. Receives the raw entry - normalizing with
 * formatPropertyName is left to each caller, because it is right for
 * name-derived properties and meaningless for shapefile keys.
 */
type IsDeletedEntry = (entry: string) => boolean;

function diffList(list: Nullable<string[]>, isDeleted: IsDeletedEntry) {
  const current = list ?? [];
  const remaining = current.filter((entry) => !isDeleted(entry));
  return { remaining, changed: remaining.length !== current.length };
}

/**
 * The focus-tile and tooltip-field purges for one entity kind, sharing the
 * read-latest-then-replace mechanics both public hooks below are built on.
 *
 * Each purge fetches the latest persisted record directly rather than trusting
 * the calling component's possibly-stale `defaults` snapshot; callers wrap them
 * in runSerialized so sequential deletes can't lose each other's removal in a
 * read-then-full-replace race. That narrows the race to concurrent edits from
 * other sessions/tabs, which would need backend support (optimistic concurrency
 * or an atomic "remove" endpoint) to close - out of scope for a frontend fix.
 */
function useSettingPurge(entityKind: EntityKind) {
  const [, dispatch] = useUserContext();
  const queryClient = useQueryClient();
  const isWell = entityKind === EntityKind.Well;

  const focusSetting = isWell
    ? DEFAULT_FOCUS_TILES_SETTING
    : DEFAULT_FACILITY_FOCUS_TILES_SETTING;
  const tooltipSetting = isWell
    ? DEFAULT_MAP_TOOLTIP_FIELDS_SETTING
    : DEFAULT_FACILITY_MAP_TOOLTIP_FIELDS_SETTING;
  const updateFocus = isWell ? updateSettingFocus : updateSettingFacilityFocus;
  const updateTooltip = isWell
    ? updateSettingMapTooltipFields
    : updateSettingFacilityMapTooltipFields;

  const purgeFocusTiles = async (isDeleted: IsDeletedEntry) => {
    const current = await getUserSetting<DefaultFocusTiles>(focusSetting);
    if (!current?.id) return;

    const { remaining, changed } = diffList(current.tiles, isDeleted);
    if (!changed) return;

    const updated = await updateUserSetting<DefaultFocusTiles>(focusSetting, {
      ...current,
      tiles: remaining
    });
    updateFocus(dispatch, updated);
    await queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);
  };

  const purgeTooltipFields = async (isDeleted: IsDeletedEntry) => {
    const current = await getUserSetting<DefaultMapTooltipFields>(tooltipSetting);
    if (!current?.id) return;

    const { remaining, changed } = diffList(current.fields, isDeleted);
    if (!changed) return;

    const updated = await updateUserSetting<DefaultMapTooltipFields>(tooltipSetting, {
      ...current,
      fields: remaining
    });
    updateTooltip(dispatch, updated);
    await queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);
  };

  return { focusSetting, tooltipSetting, purgeFocusTiles, purgeTooltipFields };
}

/**
 * Deleting a field is a deliberate, unambiguous action - unlike the
 * render-time "does this resolve to a live column" check in
 * useResolvedDefaultFieldList (which must stay read-only, since a field can
 * be merely out of context rather than deleted), it's safe to actively
 * strip the deleted field's property from a user's saved default lists
 * here. Otherwise the stale entry stays in the list forever and silently
 * comes back to life if a field with the same (name-derived) property is
 * ever recreated (EVA-6866).
 */
export default function usePurgeDeletedFieldsFromDefaults(entityKind: EntityKind) {
  const { focusSetting, tooltipSetting, purgeFocusTiles, purgeTooltipFields } =
    useSettingPurge(entityKind);

  return (deletedProperties: string[]) => {
    const validProperties = (deletedProperties ?? []).filter(Boolean);
    if (!validProperties.length) return;

    const deleted = new Set(validProperties.map(formatPropertyName));
    const isDeleted: IsDeletedEntry = (entry) => deleted.has(formatPropertyName(entry));

    runSerialized(focusSetting, () => purgeFocusTiles(isDeleted));
    runSerialized(tooltipSetting, () => purgeTooltipFields(isDeleted));
  };
}

/**
 * Purges the fields of deleted shapefiles from the saved default lists
 * (EVA-126). Deleting a shapefile is as unambiguous as deleting a field, so the
 * same reasoning as above applies - this is the root-cause counterpart to the
 * render-time guard in useIsDeletedShapefileField, which can only ever be a net
 * because it cannot judge a key stamped in another project.
 *
 * Matches on the shapefileId segment rather than the whole property string, for
 * two reasons the exact-property purge above doesn't face: one shapefile yields
 * one key per column, and the projectId stamped into a key is whichever project
 * the shapefile was picked in (buildShapefileProperty), so an org-level
 * shapefile can appear under several different projectIds in one user's list.
 *
 * Purges both entity kinds: a shapefile field can be added to either the well
 * or the facility lists, and the delete has no entity kind of its own.
 *
 * Wired into every path that deletes shapefiles outright:
 * useDeleteProjectShapefileMutation (single), useDeleteShapefileList (bulk
 * manage mode, via collectCheckedShapefileIds),
 * useDeleteProjectShapefileFolderMutation (folder cascade, via the caller's
 * folderNode and collectFolderShapefileIds) and useDeleteProjectMutation
 * (project deletion takes its owned shapefiles with it; the tree is read
 * before the delete since afterwards it is gone). Deliberately NOT wired into
 * the unlink paths - useDeleteProjectLinkedShapefile{,Folder}Mutation only
 * remove a project link, and the shapefile survives to be re-linked, so
 * purging its fields would discard a default that still works. While unlinked,
 * useIsDeletedShapefileField hides the field from the project's tooltip - the
 * two are complementary, not in conflict: the guard governs what this project
 * can show, the purge is permanent, and re-linking restores the field because
 * nothing was purged.
 *
 * All four paths infer the deleted ids client-side from the tree the caller
 * rendered from. The sturdier long-term shape is server-side: the delete
 * endpoints already compute the authoritative set of deleted shapefile ids and
 * would just need to return it.
 */
export function usePurgeDeletedShapefilesFromDefaults() {
  const well = useSettingPurge(EntityKind.Well);
  const facility = useSettingPurge(EntityKind.Facility);

  return (deletedShapefileIds: string[]) => {
    const ids = new Set((deletedShapefileIds ?? []).filter(Boolean));
    if (!ids.size) return;

    const isDeleted: IsDeletedEntry = (entry) => {
      const parsed = parseShapefileProperty(entry);
      return !!parsed && ids.has(parsed.shapefileId);
    };

    [well, facility].forEach(({ focusSetting, tooltipSetting, ...purge }) => {
      runSerialized(focusSetting, () => purge.purgeFocusTiles(isDeleted));
      runSerialized(tooltipSetting, () => purge.purgeTooltipFields(isDeleted));
    });
  };
}
